import React from 'react';
import SEO from '../components/SEO';
import { ArrowLeft } from 'lucide-react';
import { Link } from 'react-router-dom';

const Terms = () => {
  return (
    <>
      <SEO 
        title="Terms of Service | Symbionic"
        description="Terms of Service for VerifAEye — AI-assisted insurance claim documentation for Australian policyholders."
        canonical="https://symbionic.com.au/terms"
      />
      <div className="min-h-screen bg-slate-50 py-12">
        <div className="max-w-4xl mx-auto px-6">
          {/* Back button */}
          <Link 
            to="/" 
            className="inline-flex items-center text-cyan-600 hover:text-cyan-700 mb-8 group"
          >
            <ArrowLeft className="w-4 h-4 mr-2 group-hover:-translate-x-1 transition-transform" />
            Back to Home
          </Link>

          {/* Content */}
          <div className="bg-white rounded-2xl shadow-lg p-8 lg:p-12">
            <h1 className="text-4xl lg:text-5xl font-bold text-slate-900 mb-4">
              Terms of Service
            </h1>
            <p className="text-slate-600 mb-12">
              <strong>Effective date:</strong> 27 December 2025
            </p>

            <div className="prose prose-slate max-w-none space-y-8">
              <p className="text-lg text-slate-700 leading-relaxed">
                These Terms of Service ("Terms") govern your use of VerifAEye (the "Service"), operated by Bruce Lawrence Visser trading as Symbionic (ABN 88 549 501 774) ("we", "us", "our"). By creating an account or using the Service you agree to these Terms.
              </p>

              <section>
                <h2 className="text-2xl font-bold text-slate-900 mb-4">1. About the Service</h2>
                <p className="text-slate-700 leading-relaxed">
                  VerifAEye is a documentation and analysis tool that helps you photograph, organise, and describe property and contents for the purpose of preparing insurance claims and personal records. The Service uses artificial intelligence to analyse images you upload and generate descriptions, condition notes, and reports.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-slate-900 mb-4">2. Important Disclaimers</h2>
                <ul className="list-disc pl-6 space-y-3 text-slate-700">
                  <li>
                    <strong>We are not your insurer, broker, adviser, or advocate.</strong> The Service does not provide insurance, legal, or financial advice. Outputs generated by the Service are informational aids only.
                  </li>
                  <li>
                    <strong>No guarantee of claim outcomes.</strong> Using the Service does not guarantee that any insurance claim will be accepted, paid, or settled at any particular value. Claim decisions are made solely by your insurer under your policy terms.
                  </li>
                  <li>
                    <strong>AI outputs may contain errors.</strong> AI-generated descriptions and analyses can be incomplete or inaccurate. You are responsible for reviewing all outputs and correcting them before relying on them or submitting them to any third party.
                  </li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-slate-900 mb-4">3. Eligibility and Accounts</h2>
                <p className="text-slate-700 leading-relaxed">
                  You must be at least 18 years old and capable of forming a binding contract. You are responsible for the accuracy of your account information and for keeping your login credentials secure. You are responsible for all activity under your account.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-slate-900 mb-4">4. Your Content and Storage</h2>
                <p className="text-slate-700 leading-relaxed mb-3">
                  You retain ownership of all images and information you upload ("Your Content"). You grant us a limited licence to process Your Content solely to provide the Service (including transmitting images to our AI analysis providers).
                </p>
                <p className="text-slate-700 leading-relaxed">
                  The Service stores your images in your own Google Drive account where you connect one. You are responsible for your Google account, its security, and its available storage. We do not maintain a copy of your images on our servers beyond transient processing. If you disconnect Google Drive or delete files from it, we cannot recover them.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-slate-900 mb-4">5. Acceptable Use</h2>
                <p className="text-slate-700 leading-relaxed mb-3">You must not use the Service to:</p>
                <ul className="list-disc pl-6 space-y-2 text-slate-700">
                  <li>Create, alter, or present evidence in a false or misleading way, including in support of a fraudulent or exaggerated insurance claim. Insurance fraud is a criminal offence.</li>
                  <li>Upload content that is unlawful, infringes another person's rights, or contains malware.</li>
                  <li>Reverse engineer, scrape, resell, or interfere with the Service.</li>
                  <li>Circumvent usage limits or payment requirements.</li>
                </ul>
                <p className="text-slate-700 leading-relaxed mt-3">
                  We may suspend or terminate accounts engaged in prohibited conduct.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-slate-900 mb-4">6. Credits and Payments</h2>
                <p className="text-slate-700 leading-relaxed mb-3">
                  The Service offers a free tier and paid credit packs. Payments are processed by Stripe in Australian dollars; we do not store your card details. Credits are consumed when analyses are performed and, except as required by law, are non-transferable.
                </p>
                <p className="text-slate-700 leading-relaxed">
                  Nothing in these Terms excludes, restricts, or modifies any consumer guarantee, right, or remedy you have under the Australian Consumer Law that cannot lawfully be excluded. If the Service fails to meet a consumer guarantee, you are entitled to the remedies provided by law.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-slate-900 mb-4">7. Third-Party Services</h2>
                <p className="text-slate-700 leading-relaxed">
                  The Service integrates with third-party services including Google Drive (storage), Stripe (payments), and AI model providers (image analysis). Your use of those services is subject to their own terms and privacy policies. We are not responsible for the acts or omissions of third-party providers, to the extent permitted by law.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-slate-900 mb-4">8. Intellectual Property</h2>
                <p className="text-slate-700 leading-relaxed">
                  The Service, including its software, design, branding, and content (excluding Your Content), is owned by us or our licensors. These Terms do not transfer any of our intellectual property to you.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-slate-900 mb-4">9. Privacy</h2>
                <p className="text-slate-700 leading-relaxed">
                  Our collection and handling of personal information is described in our Privacy Policy. By using the Service you consent to that handling.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-slate-900 mb-4">10. Liability</h2>
                <p className="text-slate-700 leading-relaxed mb-3">
                  To the maximum extent permitted by law, and subject to the Australian Consumer Law:
                </p>
                <ul className="list-disc pl-6 space-y-2 text-slate-700">
                  <li>The Service is provided "as is" and we do not warrant it will be uninterrupted or error-free.</li>
                  <li>We are not liable for indirect or consequential loss, loss of profit, or loss arising from your reliance on AI-generated outputs without review, or from decisions made by insurers or other third parties.</li>
                  <li>Where liability cannot be excluded but can be limited, our liability is limited to re-supplying the Service or refunding the amount you paid for the affected credits.</li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-slate-900 mb-4">11. Termination</h2>
                <p className="text-slate-700 leading-relaxed">
                  You may close your account at any time. We may suspend or terminate the Service or your account for breach of these Terms, unlawful conduct, or where required by law. Unused free-tier allowances lapse on termination; statutory rights regarding paid credits are unaffected.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-slate-900 mb-4">12. Changes to the Service and Terms</h2>
                <p className="text-slate-700 leading-relaxed">
                  We may update the Service and these Terms from time to time. Material changes will be notified via the Service or email, and continued use after notice constitutes acceptance. The current version will always be available at this page.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-slate-900 mb-4">13. Governing Law</h2>
                <p className="text-slate-700 leading-relaxed">
                  These Terms are governed by the laws of Queensland, Australia, and you submit to the non-exclusive jurisdiction of its courts.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-slate-900 mb-4">14. Contact</h2>
                <p className="text-slate-700 leading-relaxed">
                  Questions about these Terms: <a href="mailto:symbionic@gmail.com" className="text-cyan-600 hover:text-cyan-700 font-semibold">symbionic@gmail.com</a>
                </p>
              </section>
            </div>

            {/* Back to top */}
            <div className="mt-12 pt-8 border-t border-slate-200 text-center">
              <Link 
                to="/" 
                className="inline-flex items-center text-cyan-600 hover:text-cyan-700 font-semibold"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Return to Home
              </Link>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Terms;
